import React, { useState, useEffect } from 'react';
import { Navigate, useParams } from "react-router-dom";
import Grid from '@mui/material/Grid2';

import { 
  Character,
} from "@dndModels";
import { DATA_TYPE_INFO } from "@dndModels/Constants";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import LoadingBox from "@components/input/LoadingBox";
import ButtonBox from "@components/input/buttons/ButtonBox";
import CharacterSheetPDF from "@dndComponents/character/pdf/CharacterSheetPDF";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import CharacterSpellsPDF from "@dndComponents/character/pdf/CharacterSpellsPDF";
import { DisablableOption } from "@models";
// import CheckBox from '../../../../components/input/CheckBox';

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

let sheet: any = null;

export default function CharacterPrintable() {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [loading_message, setLoadingMessage] = useState("");
  const [percentage, setPercentage] = useState(0);
  const [fetched, setFetched] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [logging_in, setLoggingIn] = useState(false);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [obj, setObj] = useState<Character>(new Character());
  const [selected_option, setSelectedOption] = useState("Character");
  const [selected_minion, setSelectedMinion] = useState("");
  const [selected_spells, setSelectedSpells] = useState<string[]>([]);
  // const [card_view, setCardView] = useState(false);
  const [minion_options, setMinionOptions] = useState<string[]>([]);
  const [spell_options, setSpellOptions] = useState<DisablableOption[]>([]);
  
  // eslint-disable-next-line
  useEffect(() => {
    if (!fetched) {
      if (api.user === null) {
        const userObjStr = localStorage.getItem("loginUser");
        if (!userObjStr) {
          setNavigateTo("/");
        } else {
          setLoggingIn(true);
          setLoadingMessage("Logging in");
          check_login();
        }
      } else {
        load();
      }
    }
  });
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const check_login = () => {
    setTimeout(() => {
      if (api.user === null) {
        check_login();
      } else {
        load();
      }
    }, 1000);
  }

  const load = () => {
    setLoading(true);
    api.getSetOfObjects({ data_types: ["resource"] }).then(() => {
      if (id && id !== "" && obj._id !== id) {
        load_object(id);
      } else {
        setLoading(false);
      }
    });
    setLoggingIn(false);
    setFetched(true);
  }

  // Loads the editing Character into state
  const load_object = (id: string) => {
    const onProgress = (data_type: string, percent: number) => {
      const dt_info = DATA_TYPE_INFO.find(o => o.data_type === data_type);
      if (dt_info) {
        setPercentage(percent * 100);
        setLoadingMessage(`Loading ${dt_info.plural}`);
      }
    };
    char_util.getCharacter({ id, onProgress }).then(res => {
      if (res) {
        setObj(res);
        
        if (res.minions.length > 0) {
          setMinionOptions(common_util.unique_array(res.minions.map(o => o.name)));
        }
        if (res.spells.length > 0) {
          const new_options: DisablableOption[] = [];
          const char_spells = res.spells.sort((a, b) => {
            if (a.level === b.level) {
              return a.name.localeCompare(b.name);
            }
            return a.level - b.level;
          });
          const levels = common_util.unique_array(char_spells.map(o => o.level));
          for (const level of levels) {
            if (level === 0) {
              new_options.push({ id: `${level}`, text: "Cantrip", disabled: true });
            } else if (level === 1) {
              new_options.push({ id: `${level}`, text: "1st Level", disabled: true });
            } else if (level === 2) {
              new_options.push({ id: `${level}`, text: "2nd Level", disabled: true });
            } else if (level === 3) {
              new_options.push({ id: `${level}`, text: "3rd Level", disabled: true });
            } else {
              new_options.push({ id: `${level}`, text: `${level}th Level`, disabled: true });
            }
            new_options.push(...char_spells.filter(o => o.level === level).map(o => {
              const opt: DisablableOption = {
                id: o._id, text: o.name
              };
              return opt;
            }));
          }
          setSpellOptions(new_options);
        }
        setLoading(false);
      }
    });
  }

  const renderSelectedOption = () => {
    switch (selected_option) {
      case "Character": 
        return (
          <CharacterSheetPDF
            character={obj}
            char_int={obj}
            height={window.innerHeight}
          />
        );
      case "Transformed": 
        if (obj.transform) {
          return (
            <CharacterSheetPDF
              character={obj}
              char_int={obj.transform}
              height={window.innerHeight}
            />
          );
        }
      break;
      case "Minion": 
        const minion = obj.minions.find(o => o.name === selected_minion);
        if (minion) {
          return (
            <CharacterSheetPDF
              // character={obj}
              char_int={minion} 
              height={window.innerHeight}
            />
          );
        }
      break;
      case "Spells": 
        const spells = obj.spells.filter(o => selected_spells.includes(o._id));
        return (
          <CharacterSpellsPDF
            char={obj}
            height={window.innerHeight}
            spells={spells}
            card_view={false}
            // ={card_view}
          />
        );
    }
    return null;
  }

  if (!fetched || logging_in || loading) {
    const height = window.innerHeight - 64;
    return (<LoadingBox message={loading_message} percentage={percentage} size={height * 0.5} height={height} width={window.innerWidth} />);
  } else if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else if (logging_in) {
    return <span>Logging in</span>;
  } else if (api.user === null) {
    return <Navigate to="/" />;
  } else { 
    const options = ["Character"];
    if (obj.transform) {
      options.push("Transformed");
    }
    if (minion_options.length > 0) {
      options.push("Minion");
    }
    if (spell_options.length > 0) {
      options.push("Spells");
    }
    return (
      <Grid container spacing={0} direction="row">
        { options.length > 1 &&
          <Grid
            size={{
              xs: 3
            }}>
            <SelectStringBox
              name="Print Option"
              options={options}
              value={selected_option}
              onChange={setSelectedOption}
            />
          </Grid>
        }
        { selected_option === "Minion" &&
          <Grid
            size={{
              xs: 3
            }}>
            <SelectStringBox
              name="Minion"
              options={minion_options}
              value={selected_minion}
              onChange={setSelectedMinion}
            />
          </Grid>
        }
        { selected_option === "Spells" &&
          <>
            <Grid
              size={{
                xs: 3
              }}>
              <SelectStringBox
                name="Spells"
                options={spell_options}
                values={selected_spells}
                multiple
                onChange={setSelectedSpells}
              />
            </Grid>
            {/* <Grid item xs={3}>
              <CheckBox
                name="Card View"
                value={card_view}
                onChange={setCardView}
              />
            </Grid> */}
          </>
        }
        <Grid
          size={{
            xs: options.length > 1 ? (selected_option === "Minion" ? 6 : 9) : 12
          }}>
          <ButtonBox
            name="Download (don't worry about the layout on this page, it looks better in the pdf)"
            onClick={() => {
              if (sheet) {
                sheet.save();
              }
            }} 
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <div style={{ width: "794px", marginLeft: "calc((100% - 794px)/2)" }}>
            {/* <PDFExport paperSize={"Letter"} fileName={`${obj.name}_${selected_option}.pdf`} ref={(r) => sheet = r }> */}
              { renderSelectedOption() }
            {/* </PDFExport> */}
          </div>
        </Grid>
      </Grid>
    ); 
  }
}
